"use client";

import dynamic from "next/dynamic";
import { useLayoutEffect, useState } from "react";
import {
  calculateMatching,
  calculateDetailedMatching,
  downloadMatchingReport,
} from "@/lib/api/kundali";
import type { PersonInput, MatchingResponse, DetailedMatchingResponse } from "@/lib/api/kundali";
import type { KundaliRequest } from "@ishvaram/api-client";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import { trackCalculatorUsed, trackMatchingCompleted } from "@/lib/analytics-free-tools";
import type { MatchingResultsPanelProps } from "@/components/matching/MatchingResultsPanel";

// Post-submission result rendering lives in the shared MatchingResultsPanel —
// the same component tree the /match/<id> share surface renders (rule 46).
const loadMatchingResultsPanel = () =>
  import("@/components/matching/MatchingResultsPanel").then((mod) => mod.MatchingResultsPanel);

const MatchingResultsPanel = dynamic<MatchingResultsPanelProps>(loadMatchingResultsPanel, {
  ssr: false,
  loading: () => <MatchingResultsSkeleton />,
});

function preloadMatchingResultsPanel() {
  void loadMatchingResultsPanel();
}

export default function KundliMatchingClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [personAData, setPersonAData] = useState<PersonInput | null>(null);
  const [personBData, setPersonBData] = useState<PersonInput | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchingResponse | null>(null);

  // Detailed matching state
  const [detailedResult, setDetailedResult] = useState<DetailedMatchingResponse | null>(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedError, setDetailedError] = useState<string | null>(null);

  // PDF download state
  const [pdfLoading, setPdfLoading] = useState(false);

  // Step tracking: 0 = enter A, 1 = enter B, 2 = results
  const step = result ? 2 : personAData ? 1 : 0;

  // CLS fix (field CrUX p75 ≈ 0.41 desktop): when the result arrives the form
  // (MatchingFormSteps) collapses to null and the much taller MatchingResultsPanel
  // renders inline above the always-present SEO content — past CLS's 500ms input
  // window, so the SEO block gets shoved down and scores a large shift. Anchoring
  // the swap to the top of the viewport before paint keeps that reflow below the
  // fold. (On /match/<id> the panel is the whole page — no form, no swap.)
  const hasResult = Boolean(result);
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !hasResult) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [hasResult]);

  async function handleSubmitA(data: KundaliRequest) {
    preloadMatchingResultsPanel();
    setPersonAData({
      name: data.name,
      birth_date: data.birth_date,
      birth_time: data.birth_time,
      birth_place: data.birth_place,
      latitude: data.latitude ?? undefined,
      longitude: data.longitude ?? undefined,
      timezone: data.timezone ?? undefined,
    });
  }

  async function handleSubmitB(data: KundaliRequest) {
    if (!personAData) return;
    preloadMatchingResultsPanel();

    const personB: PersonInput = {
      name: data.name,
      birth_date: data.birth_date,
      birth_time: data.birth_time,
      birth_place: data.birth_place,
      latitude: data.latitude ?? undefined,
      longitude: data.longitude ?? undefined,
      timezone: data.timezone ?? undefined,
    };
    setPersonBData(personB);
    setLoading(true);
    setError(null);

    try {
      const response = await calculateMatching({
        person_a: personAData,
        person_b: personB,
        language: defaultLanguage,
      });
      setResult(response);
      trackCalculatorUsed({ calculator: "kundli-matching" });
      trackMatchingCompleted({ score: response.total_score ?? 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  async function handleGetDetailed() {
    if (!personAData || !personBData) return;
    setDetailedLoading(true);
    setDetailedError(null);

    try {
      const response = await calculateDetailedMatching({
        person_a: personAData,
        person_b: personBData,
        match_id: result?.match_id ?? null,
      });
      setDetailedResult(response);
      trackCalculatorUsed({ calculator: "kundli-matching-detailed" });
    } catch (err) {
      setDetailedError(err instanceof Error ? err.message : "Failed to generate detailed report");
    } finally {
      setDetailedLoading(false);
    }
  }

  async function handleDownloadPdf() {
    if (!personAData || !personBData) return;
    setPdfLoading(true);

    try {
      const blob = await downloadMatchingReport({
        person_a: personAData,
        person_b: personBData,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Matching_Report_${personAData.name}_${personBData.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Silently fail for PDF download — user can retry
    } finally {
      setPdfLoading(false);
    }
  }

  function handleReset() {
    setPersonAData(null);
    setPersonBData(null);
    setResult(null);
    setDetailedResult(null);
    setDetailedError(null);
    setError(null);
  }

  return (
    <>
      <MatchingFormSteps
        step={step}
        personAData={personAData}
        loading={loading}
        error={error}
        defaultLanguage={defaultLanguage}
        onSubmitA={handleSubmitA}
        onSubmitB={handleSubmitB}
        onChangePersonA={() => {
          setPersonAData(null);
          setPersonBData(null);
        }}
      />

      {loading && <MatchingLoading />}
      <MatchingSubmitError error={error} step={step} loading={loading} />
      {result && (
        <MatchingResultsPanel
          surface="tool"
          result={result}
          personAData={personAData}
          personBData={personBData}
          defaultLanguage={defaultLanguage}
          detailedResult={detailedResult}
          detailedLoading={detailedLoading}
          detailedError={detailedError}
          pdfLoading={pdfLoading}
          onGetDetailed={handleGetDetailed}
          onDownloadPdf={handleDownloadPdf}
          onReset={handleReset}
        />
      )}
    </>
  );
}

function MatchingFormSteps({
  step,
  personAData,
  loading,
  error,
  defaultLanguage,
  onSubmitA,
  onSubmitB,
  onChangePersonA,
}: {
  step: number;
  personAData: PersonInput | null;
  loading: boolean;
  error: string | null;
  defaultLanguage: "en" | "hi";
  onSubmitA: (data: KundaliRequest) => void;
  onSubmitB: (data: KundaliRequest) => void;
  onChangePersonA: () => void;
}) {
  const hi = defaultLanguage === "hi";
  if (step === 0) {
    return (
      <div className="max-w-md mx-auto">
        <BirthDetailsForm
          onSubmit={onSubmitA}
          loading={false}
          submitLabel={hi ? "Next: Person B details" : "Next: Enter Person B"}
          heading={hi ? "Person A" : "Person A (Boy)"}
          language={defaultLanguage}
          analyticsToolName="kundli-matching"
          analyticsStep="person_a"
        />
      </div>
    );
  }
  if (step !== 1 || !personAData) return null;
  return (
    <div className="space-y-4">
      <PersonSummary personAData={personAData} onChange={onChangePersonA} />
      <BirthDetailsForm
        onSubmit={onSubmitB}
        loading={loading}
        submitLabel={hi ? "Gun Milan calculate karein" : "Calculate Gun Milan"}
        error={error ?? undefined}
        heading={hi ? "Person B" : "Person B (Girl)"}
        language={defaultLanguage}
        analyticsToolName="kundli-matching"
        analyticsStep="person_b"
      />
    </div>
  );
}

function PersonSummary({
  personAData,
  onChange,
}: {
  personAData: PersonInput;
  onChange: () => void;
}) {
  return (
    <div className="card-sacred p-4 flex items-center justify-between">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-ish-text-faint">
          Person A
        </p>
        <p className="text-[15px] font-bold text-ish-dark">{personAData.name}</p>
        <p className="text-[12px] text-ish-text-muted">
          {personAData.birth_date} &middot; {personAData.birth_place}
        </p>
      </div>
      <button onClick={onChange} className="text-[12px] font-semibold text-ish-accent">
        Change
      </button>
    </div>
  );
}

function MatchingLoading() {
  return (
    <div className="text-center py-12">
      <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto border-ish-accent" />
      <p className="mt-4 text-[14px] text-ish-text-muted">Calculating Ashtakoot Gun Milan...</p>
      <p className="mt-1 text-[12px] text-ish-text-faint">
        Generating kundalis and matching all 8 kootas
      </p>
    </div>
  );
}

function MatchingSubmitError({
  error,
  step,
  loading,
}: {
  error: string | null;
  step: number;
  loading: boolean;
}) {
  if (!error || step !== 1 || loading) return null;
  return (
    <div className="mt-4 text-[13px] rounded-lg p-3.5 text-center text-error bg-error/5 border border-error/20">
      {error}
    </div>
  );
}

function MatchingResultsSkeleton() {
  return (
    <div className="min-h-screen space-y-4" aria-hidden="true">
      <div className="card-sacred p-6 sm:p-8">
        <div className="mx-auto h-24 w-24 rounded-full bg-[var(--ish-border)] animate-pulse" />
        <div className="mx-auto mt-5 h-5 w-40 rounded bg-[var(--ish-border)] animate-pulse" />
        <div className="mx-auto mt-3 h-3 w-56 rounded bg-[var(--ish-border)] animate-pulse" />
      </div>
      <div className="card-sacred p-5 sm:p-6 space-y-3">
        <div className="h-4 w-36 rounded bg-[var(--ish-border)] animate-pulse" />
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-16 rounded-lg bg-[var(--ish-border)] animate-pulse" />
          ))}
        </div>
      </div>
      <div className="card-sacred p-5 sm:p-6">
        <div className="h-4 w-44 rounded bg-[var(--ish-border)] animate-pulse" />
        <div className="mt-4 h-28 rounded-lg bg-[var(--ish-border)] animate-pulse" />
      </div>
    </div>
  );
}
